import { useEffect, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { Plus, PencilSimple, Trash, ArrowsClockwise } from "@phosphor-icons/react";

const EMPTY = { name: "", prompt: "" };

export default function EmailPrompts() {
  const [prompts, setPrompts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialog, setDialog] = useState(false);
  const [editId, setEditId] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get("/email-prompts");
      setPrompts(data);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const openCreate = () => { setEditId(null); setForm(EMPTY); setDialog(true); };
  const openEdit = (p) => { setEditId(p.id); setForm({ name: p.name, prompt: p.prompt }); setDialog(true); };

  const save = async () => {
    if (!form.name.trim() || !form.prompt.trim())
      return toast.error("Name und Prompt sind erforderlich");
    setSaving(true);
    try {
      if (editId) {
        const { data } = await api.patch(`/email-prompts/${editId}`, form);
        setPrompts((ps) => ps.map((p) => (p.id === editId ? data : p)));
        toast.success("Prompt aktualisiert");
      } else {
        const { data } = await api.post("/email-prompts", form);
        setPrompts((ps) => [...ps, data]);
        toast.success("Prompt angelegt");
      }
      setDialog(false);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Speichern fehlgeschlagen");
    } finally {
      setSaving(false);
    }
  };

  const remove = async (p) => {
    if (!window.confirm(`Prompt „${p.name}" löschen?`)) return;
    await api.delete(`/email-prompts/${p.id}`);
    setPrompts((ps) => ps.filter((x) => x.id !== p.id));
    toast.success("Prompt gelöscht");
  };

  const fmt = (iso) => {
    try { return new Date(iso).toLocaleString("de-DE", { dateStyle: "medium", timeStyle: "short" }); }
    catch { return iso; }
  };

  return (
    <div className="p-8 max-w-6xl">
      <div className="flex flex-wrap items-end justify-between gap-4 mb-8">
        <div>
          <h1 className="font-heading text-4xl font-bold tracking-tight">E-Mail Prompts</h1>
          <p className="text-sm text-zinc-500 mt-1">
            Gespeicherte KI-Prompts verwalten. Bei der E-Mail-Generierung unter „Leads" wählst du einen davon aus.
          </p>
        </div>
        <Button onClick={openCreate} data-testid="add-prompt-button" className="bg-[#002FA7] hover:bg-[#001D6E] text-white rounded-sm">
          <Plus size={16} className="mr-2" /> Prompt hinzufügen
        </Button>
      </div>

      {loading ? (
        <div className="text-sm text-zinc-400 py-12 text-center">Lade…</div>
      ) : prompts.length === 0 ? (
        <div className="bg-white border border-zinc-200 rounded-md p-12 text-center text-zinc-500" data-testid="prompts-empty">
          Noch kein Prompt vorhanden. Lege deinen ersten Prompt an.
        </div>
      ) : (
        <div className="bg-white border border-zinc-200 rounded-md overflow-x-auto" data-testid="prompts-table">
          <table className="w-full text-sm border-collapse min-w-[800px]">
            <thead>
              <tr className="bg-zinc-50 text-left text-xs uppercase tracking-wider text-zinc-500">
                <th className="px-4 py-3 font-medium">Name</th>
                <th className="px-4 py-3 font-medium">Prompt</th>
                <th className="px-4 py-3 font-medium">Erstellt</th>
                <th className="px-4 py-3 font-medium text-right">Aktionen</th>
              </tr>
            </thead>
            <tbody>
              {prompts.map((p) => (
                <tr key={p.id} data-testid="prompt-row" className="border-t border-zinc-100 hover:bg-zinc-50">
                  <td className="px-4 py-3 font-medium whitespace-nowrap">{p.name}</td>
                  <td className="px-4 py-3 text-zinc-600 max-w-[560px]">
                    <div className="truncate" title={p.prompt}>{p.prompt}</div>
                  </td>
                  <td className="px-4 py-3 text-zinc-500 text-xs whitespace-nowrap">{fmt(p.created_at)}</td>
                  <td className="px-4 py-3">
                    <div className="flex items-center justify-end gap-1">
                      <Button size="sm" variant="ghost" onClick={() => openEdit(p)} data-testid={`edit-prompt-${p.id}`} className="h-8 text-xs">
                        <PencilSimple size={15} className="mr-1" /> Bearbeiten
                      </Button>
                      <button onClick={() => remove(p)} data-testid={`delete-prompt-${p.id}`} className="ml-1 text-zinc-300 hover:text-[#E53935]">
                        <Trash size={16} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <Dialog open={dialog} onOpenChange={setDialog}>
        <DialogContent className="max-w-2xl" data-testid="prompt-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading">{editId ? "Prompt bearbeiten" : "Prompt hinzufügen"}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label className="text-xs text-zinc-600">Name *</Label>
              <Input value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} placeholder="z.B. Gastro-Neueröffnung" className="mt-1 h-9" data-testid="prompt-name-input" />
            </div>
            <div>
              <Label className="text-xs text-zinc-600">Prompt *</Label>
              <Textarea value={form.prompt} onChange={(e) => setForm((f) => ({ ...f, prompt: e.target.value }))} rows={12} className="mt-1 text-sm" data-testid="prompt-text-input" />
              <p className="text-xs text-zinc-400 mt-1">
                Die KI erhält zusätzlich automatisch die Lead-Daten (Firma, Markenname, Ort, Beschreibung …) und schreibt Betreff + Text.
                Die Signatur des Postfachs wird beim Versand automatisch angehängt.
              </p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialog(false)} className="rounded-sm">Abbrechen</Button>
            <Button onClick={save} disabled={saving} data-testid="save-prompt-button" className="bg-[#002FA7] hover:bg-[#001D6E] text-white rounded-sm">
              {saving && <ArrowsClockwise size={16} className="mr-2 animate-spin" />}
              Speichern
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
